import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useLazyGetCashbackSuggestionQuery } from "../../../../services/transactions/transactionsApi";
import { takesCashbackRate } from "../../../../utils/cashback";
import {
  CASHBACK_EARNED_HELPER,
  OLDER_CASHBACK_EARNED_HELPER,
} from "./constants";
import {
  TransactionTypeOptions,
  UseCashbackEarnedReturn,
  UseCashbackFormParams,
} from "./types";
import { ISO_DATE, isoDateOf, useCashbackPrefill } from "./useCashbackPrefill";

export const useCashbackEarned = ({
  form,
  accounts,
  action,
  original,
}: UseCashbackFormParams): UseCashbackEarnedReturn => {
  const [type, from, amount, watchedDate, recurring, earned] = useWatch({
    control: form.control,
    name: [
      "transactionType",
      "fromAccount",
      "amount",
      "transactionDate",
      "isRecurring",
      "cashbackEarned",
    ],
  });
  const date = isoDateOf(watchedDate);
  const [suggest] = useLazyGetCashbackSuggestionQuery();
  const {
    error,
    clearError,
    isUnchangedEdit,
    isManuallyEdited,
    isPending,
    write,
    prefill,
  } = useCashbackPrefill({ form, action });
  const fromAccount = accounts?.find((a) => String(a.id) === from?.value);
  const isEarnedVisible =
    !recurring &&
    type?.value === TransactionTypeOptions.EXPENSE &&
    takesCashbackRate(fromAccount?.cashbackMode);
  const earnedLabel = isEarnedVisible ? "Cashback earned" : undefined;
  // Mirrors the API: an expense saved before linked incomes keeps its cashback as a note while it stays on its account.
  const isOlderCashback =
    action === "edit" &&
    original?.isCashbackCredited === false &&
    Number(original.cashbackEarned ?? 0) > 0 &&
    from?.value === original.fromAccount?.value;
  const earnedHelper = isOlderCashback
    ? OLDER_CASHBACK_EARNED_HELPER
    : CASHBACK_EARNED_HELPER;
  const expenseKey = JSON.stringify([
    type?.value,
    from?.value,
    amount,
    date,
    recurring,
    fromAccount?.cashbackMode,
  ]);
  const accountsLoaded = accounts !== undefined;
  const previousFrom = useRef(original?.fromAccount?.value);

  useEffect(() => {
    if (!accountsLoaded) {
      return;
    }
    const isUnchanged = isUnchangedEdit(expenseKey);
    const moved = previousFrom.current !== from?.value;
    previousFrom.current = from?.value;
    clearError();
    if (isUnchanged) {
      return;
    }
    if (!earnedLabel || !from) {
      const shouldClear =
        action !== "edit" ||
        moved ||
        recurring ||
        type?.value !== TransactionTypeOptions.EXPENSE;
      if (shouldClear) {
        write(null, { shouldDirty: false });
      }
      return;
    }
    if (moved) {
      write(null);
    }
    const hasValidInputs = Number(amount) > 0 && ISO_DATE.test(date);
    if (isManuallyEdited() || !hasValidInputs) {
      return;
    }
    const accountId = Number(from.value);
    return prefill({
      key: expenseKey,
      delayMs: 200,
      load: () =>
        suggest({
          accountId,
          amount: Number(amount),
          transactionDate: date,
          excludeTransactionId: action === "edit" ? original?.id : undefined,
        }).unwrap(),
      apply: (result) =>
        write(
          result.cashbackEarned == null ? null : String(result.cashbackEarned),
        ),
      errorMessage: "Couldn't load earned cashback. Enter it manually.",
    });
  }, [
    accountsLoaded,
    expenseKey,
    earnedLabel,
    action,
    original?.id,
    clearError,
    isUnchangedEdit,
    isManuallyEdited,
    write,
    prefill,
    suggest,
  ]);

  return {
    cashbackEarnedLabel: earnedLabel,
    cashbackEarnedHelper: isEarnedVisible ? earnedHelper : undefined,
    error,
    isPending: isPending(expenseKey, earned),
  };
};
